import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  styled,
  Typography
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarOutlineRoundedIcon from "@material-ui/icons/StarOutlineRounded";
import StarRoundedIcon from "@material-ui/icons/StarRounded";
import { useQuery } from "@redux-requests/react";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Header, Loader, NoCourseData } from "../../components";
import { createFavourite, getCourses } from "../../ducks/actions";
import * as types from "../../ducks/types";
import { useActionRequest } from "../../hooks";
import { ReactComponent as CoursesSvg } from "../../assets/icons/courses.svg";

const useStyles = makeStyles(theme => ({
  progressCellContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    [theme.breakpoints.down("md")]: { flexDirection: "column" }
  },
  progressCellLabel: {
    [theme.breakpoints.up("lg")]: { minWidth: "6rem" }
  },
  checkIcon: {
    fontSize: "2rem"
  },
  gridRow: {
    marginBottom: "0.5rem",
    cursor: "pointer"
  },
  gridCell: {
    padding: "1rem"
  },
  gridCellIcon: {
    display: "flex",
    justifyContent: "center"
  }
}));

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  width: "10rem"
}));

export default function CoursesPage() {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const openCourse = courseId => () => history.push(`courses/${courseId}`);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getCourses({}));
  }, [dispatch]);

  const {
    data: { courses },
    loading: coursesLoading
  } = useQuery({ type: types.GET_COURSES, defaultData: { courses: [] } });

  const createFavouriteRequest = useActionRequest({
    action: createFavourite
  });

  const onCreateFavourite = id => event => {
    event.stopPropagation();
    createFavouriteRequest({ favouritableId: id, favouritableType: "Course" });
  };

  const downloadPdf = urlPdf => event => {
    event.stopPropagation();
    window.open(urlPdf, "_blank");
  };

  return (
    <>
      <Loader loading={coursesLoading}>
        <Header
          icon={CoursesSvg}
          title={intl.formatMessage({
            id: "Pages.Courses.Courses",
            defaultMessage: "Courses"
          })}
          helper={intl.formatMessage({
            id: "Pages.Courses.Description",
            defaultMessage: "test description"
          })}
        />
        <NoCourseData data={courses}>
          <Grid container className={classes.gridContainer}>
            {courses.map(value => (
              <Paper
                key={value.id}
                alignItems="center"
                component={Grid}
                className={classes.gridRow}
                container
                item
                onClick={openCourse(value.id)}
              >
                <Grid className={classes.gridCellIcon} item xs={12} sm={1}>
                  {value.is_favourite ? (
                    <StarRoundedIcon className={classes.checkIcon} color="primary" />
                  ) : (
                    <IconButton onClick={onCreateFavourite(value.id)}>
                      <StarOutlineRoundedIcon className={classes.checkIcon} color="primary" />
                    </IconButton>
                  )}
                </Grid>
                <Grid container item justify="center" alignItems="center" xs={12} sm={11}>
                  <Grid className={classes.gridCell} item xs={12} sm={6} lg={4}>
                    <Typography variant="body1">
                      <strong>{value.name}</strong>
                    </Typography>
                    <Typography variant="body1">
                      {`${value.course_modules?.length} ${intl.formatMessage({
                        id: "Pages.Courses.Modules",
                        defaultMessage: "modules"
                      })} - ${value.total_time} ${intl.formatMessage({
                        id: "Pages.Courses.TotalHours",
                        defaultMessage: "total hours"
                      })}`}
                    </Typography>
                  </Grid>
                  <Grid className={classes.gridCell} item xs={12} sm={6} lg={4}>
                    <div className={classes.progressCellContainer}>
                      <Typography className={classes.progressCellLabel} variant="body2">
                        {`${intl.formatMessage({
                          id: "Pages.Courses.Viewed",
                          defaultMessage: "viewed"
                        })}: ${value.completion_percentage}%`}
                      </Typography>
                      <span>
                        <BorderLinearProgress
                          variant="determinate"
                          value={value.completion_percentage}
                        />
                      </span>
                      {false && <CheckCircleIcon className={classes.checkIcon} color="primary" />}
                    </div>
                  </Grid>
                  <Grid className={classes.gridCell} container item justify="center" xs={12} lg={4}>
                    <Button
                      color="primary"
                      disabled={!value.certificate}
                      variant="contained"
                      onClick={downloadPdf(value.certificate?.pdf.url)}
                    >
                      {intl.formatMessage({
                        id: "Pages.Courses.DownloadCertificate",
                        defaultMessage: "Download Certificate"
                      })}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Grid>
        </NoCourseData>
      </Loader>
    </>
  );
}
